/**
 * Wizard API Service for Scheduling Flow
 * Maintains identical endpoints from Mobile-client-core wizardApiService.js
 * Handles all wizard-related API calls
 */

import { withApiErrorHandling, buildApiEndpoint } from '../utils/apiHelpers';
import apiClient from '../config/api';

/**
 * Get available dates for calendar display (optimized endpoint)
 * @param {Array} serviceIds - Array of service IDs
 * @param {number} year - Year to check
 * @param {number} month - Month to check (1-12)
 * @returns {Promise<Array>} Array of available date strings (YYYY-MM-DD)
 */
export const getAvailableDatesForCalendar = async (serviceIds, year = null, month = null) => {
  const endpoint = buildApiEndpoint('appointments/wizard/available-dates-fast');
  
  const now = new Date();
  const targetYear = year ?? now.getFullYear();
  const targetMonth = month ?? (now.getMonth() + 1);
  
  const params = {
    service_ids: serviceIds,
    year: targetYear,
    month: targetMonth,
  };
  
  return withApiErrorHandling(
    () => apiClient.get(endpoint, { params }),
    {
      defaultValue: [],
      transformData: (data) => Array.isArray(data) ? data : []
    }
  );
};

/**
 * Get available professionals for selected services on a specific date
 * @param {Array} serviceIds - Array of service IDs
 * @param {string} date - Date string (YYYY-MM-DD)
 * @returns {Promise<Array>} Array of available professionals
 */
export const getAvailableProfessionals = async (serviceIds, date) => {
  const endpoint = buildApiEndpoint('appointments/wizard/professionals');
  
  return withApiErrorHandling(
    () => apiClient.get(endpoint, {
      params: {
        service_ids: serviceIds,
        date: date,
      }
    }),
    {
      defaultValue: [],
      transformData: (data) => data?.professionals || []
    }
  );
};

/**
 * Get available time slots for booking
 * @param {Object} params - Slot request parameters
 * @param {Array} params.serviceIds - Array of service IDs
 * @param {string} params.date - Date string (YYYY-MM-DD)
 * @param {number} params.professionalsRequested - Number of professionals requested
 * @param {Array} params.professionalIds - Array of specific professional IDs (optional)
 * @returns {Promise<Array>} Array of available time slots
 */
export const getAvailableSlots = async ({
  serviceIds,
  date,
  professionalsRequested = 1,
  professionalIds = null
}) => {
  const endpoint = buildApiEndpoint('appointments/wizard/availability');
  
  const requestBody = {
    service_ids: serviceIds.map(id => String(id)), // Ensure string format
    date: date,
    professionals_requested: professionalsRequested,
    professional_ids: professionalIds && professionalIds.length > 0 ? 
      professionalIds.map(id => String(id)) : null
  };

  // Add reservation checking parameter
  const queryParams = new URLSearchParams({
    include_reservations: 'true'
  });

  return withApiErrorHandling(
    () => apiClient.post(`${endpoint}?${queryParams}`, requestBody),
    {
      defaultValue: [],
      transformData: (data) => data?.available_slots || []
    }
  );
};

/**
 * Create a multi-service appointment booking
 * @param {Object} bookingData - Complete booking information
 * @returns {Promise<Object>} Booking result
 */
export const createMultiServiceBooking = async (bookingData) => {
  const endpoint = buildApiEndpoint('appointments/wizard/book');

  return withApiErrorHandling(
    () => apiClient.post(endpoint, bookingData),
    {
      defaultValue: null,
      transformData: (data) => data
    }
  );
};

/**
 * Build booking payload for /wizard/book endpoint
 * Works for both authenticated users (with client_id) and resolved guests
 * @param {Object} params - Booking parameters
 * @param {string} params.clientId - Resolved client UUID
 * @param {Array} params.services - Selected services
 * @param {Object} params.selectedSlot - Selected time slot from availability API
 * @param {string|Date} params.date - Booking date
 * @param {Object} params.serviceAssignments - Professional assignments { serviceIndex: professionalId }
 * @param {string} params.notes - Optional notes
 * @returns {Object} Formatted booking payload for /wizard/book
 */
export const buildWizardBookingPayload = ({ clientId, services, selectedSlot, date, serviceAssignments, notes = null }) => {
  // Format date
  const formattedDate = typeof date === 'string' ? date : date.toISOString().split('T')[0];

  // Build services array matching ServiceInBooking schema
  const formattedServices = services.map((service, index) => {
    const slotService = selectedSlot.services?.[index];
    return {
      service_id: String(service.id || service.service_id),
      professional_id: String(serviceAssignments[String(index)] || slotService?.professional_id),
      station_id: slotService?.station_id || null,
      start_time: slotService?.start_time || selectedSlot.start_time,
      end_time: slotService?.end_time || selectedSlot.end_time
    };
  });

  return {
    client_id: String(clientId),
    date: formattedDate,
    selected_slot: {
      start_time: selectedSlot.start_time,
      end_time: selectedSlot.end_time,
      execution_type: selectedSlot.execution_type || 'sequential',
      services: formattedServices
    },
    notes_by_client: notes || null
  };
};

/**
 * Helper method to validate date format
 * @param {string|Date} date - Date to validate
 * @returns {string} Formatted date string (YYYY-MM-DD)
 */
export const validateDate = (date) => {
  if (typeof date === 'string') {
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(date)) {
      throw new Error('Date must be in YYYY-MM-DD format');
    }
    return date;
  }
  
  if (date instanceof Date) {
    return date.toISOString().split('T')[0];
  }
  
  throw new Error('Invalid date format');
};

/**
 * Format services for booking request
 * @param {Object} selectedSlot - Selected time slot with services
 * @returns {Array} Formatted services array
 */
export const formatServicesForBooking = (selectedSlot) => {
  return selectedSlot.services.map(service => ({
    service_id: service.service_id,
    professional_id: service.professional_id,
    station_id: service.station_id,
    start_time: service.start_time || selectedSlot.start_time,
    end_time: service.end_time || selectedSlot.end_time,
  }));
};

/**
 * Build complete booking request
 * @param {string} clientId - Client ID
 * @param {string} date - Date string (YYYY-MM-DD)
 * @param {Object} selectedSlot - Selected time slot
 * @param {string} notes - Client notes (optional)
 * @returns {Object} Complete booking request
 */
export const buildBookingRequest = (clientId, date, selectedSlot, notes = null) => {
  const services = formatServicesForBooking(selectedSlot);
  
  return {
    client_id: clientId,
    date: validateDate(date),
    selected_slot: {
      start_time: selectedSlot.start_time,
      end_time: selectedSlot.end_time,
      execution_type: selectedSlot.execution_type,
      services: services,
    },
    notes_by_client: notes,
  };
};

/**
 * Get availability using v2 API (sequential placement algorithm)
 * Used for the new per-service professional assignment flow
 * @param {Object} params - Availability parameters
 * @param {Array} params.services - Array of service objects with id and optional variation
 * @param {Object} params.assignments - Index-based mapping {"0": "prof-uuid", "1": "prof-uuid"}
 * @param {string} params.date - Target date YYYY-MM-DD
 * @param {number} params.slotInterval - Slot interval (default: 15)
 * @returns {Promise<Object>} Availability v2 response with professional slots
 */
export const getAvailabilityV2 = async ({
  services,
  assignments,
  date,
  slotInterval = 15
}) => {
  const endpoint = buildApiEndpoint('availability/v2/availability');

  // Build service_requests array from services
  const serviceRequests = services.map(service => ({
    service_id: String(service.id),
    variation_id: service.selectedVariation?.id || service.variation_id || null
  }));

  const requestBody = {
    service_requests: serviceRequests,
    assignments: assignments || {},
    target_date: date,
    slot_interval_minutes: slotInterval
  };

  return withApiErrorHandling(
    () => apiClient.post(endpoint, requestBody),
    {
      defaultValue: { professionals: [], total_professionals: 0 },
      transformData: (data) => data
    }
  );
};

/**
 * Transform v2 API response into UI-friendly slot format
 * The v2 API returns each slot with a services array that includes individual start_time/end_time
 * for each service, calculated by the backend's sequential placement algorithm.
 * @param {Object} v2Response - Response from getAvailabilityV2
 * @param {Array} services - Selected services array (for fallback data)
 * @param {Object} assignments - Service assignments (for professional lookup)
 * @returns {Array} Flat array of slots for UI display
 */
export const transformV2SlotsForUI = (v2Response, services, assignments) => {
  if (!v2Response?.professionals?.length) {
    return [];
  }

  // Build professional name lookup
  const professionalNameMap = {};
  v2Response.professionals.forEach(prof => {
    professionalNameMap[prof.professional_id] = prof.professional_name;
  });

  // Group slots by start_time - merge data from all professionals
  const slotsByTime = {};

  v2Response.professionals.forEach(professional => {
    if (!professional.available_slots) return;

    professional.available_slots.forEach(slot => {
      const timeKey = slot.start_time;
      const time = slot.start_time?.substring(0, 5) || slot.start_time; // HH:MM format

      if (!slotsByTime[timeKey]) {
        // Extract services with timing from the API response
        // The backend already calculated correct start_time/end_time for each service
        const servicesWithTiming = (slot.services || []).map((apiService, index) => {
          // Get service details from our services array
          const localService = services[index] || {};
          const professionalId = assignments[String(index)];

          return {
            service_id: apiService.service_id || localService.id,
            service_name: apiService.service_name || localService.name,
            duration_minutes: apiService.duration_minutes || localService.duration_minutes,
            price: apiService.price || localService.price,
            professional_id: apiService.professional_id || professionalId,
            professional_name: apiService.professional_name || professionalNameMap[professionalId] || 'Profissional',
            // Use timing from API - this is calculated by backend for sequential/parallel execution
            start_time: apiService.start_time,
            end_time: apiService.end_time
          };
        });

        // If no services from API, build from our services array with calculated timing
        const finalServices = servicesWithTiming.length > 0 ? servicesWithTiming :
          services.map((service, index) => {
            const professionalId = assignments[String(index)];
            return {
              service_id: service.id,
              service_name: service.name,
              duration_minutes: service.duration_minutes,
              price: service.price,
              professional_id: professionalId,
              professional_name: professionalNameMap[professionalId] || 'Profissional',
              start_time: slot.start_time,
              end_time: slot.end_time
            };
          });

        slotsByTime[timeKey] = {
          id: `slot-${time.replace(':', '')}`,
          time: time,
          start_time: slot.start_time,
          end_time: slot.end_time,
          duration_minutes: slot.duration_minutes,
          available: true,
          is_gap_slot: slot.is_gap_slot || false,
          professionals: [],
          services: finalServices
        };
      }

      // Add professional info to this time slot
      slotsByTime[timeKey].professionals.push({
        professional_id: professional.professional_id,
        professional_name: professional.professional_name
      });
    });
  });

  // Convert to array and sort by time
  return Object.values(slotsByTime).sort((a, b) =>
    a.time.localeCompare(b.time)
  );
};

/**
 * Build booking payload for v2 API format (same as Web-admin)
 * @param {Object} client - Client data
 * @param {Array} services - Selected services
 * @param {Object} selectedSlot - Selected time slot with services
 * @param {string} date - Appointment date
 * @param {Object} assignments - Service assignments
 * @param {string} notes - Optional notes
 * @returns {Object} Booking request payload
 */
export const buildBookingPayloadV2 = (client, services, selectedSlot, date, assignments, notes = null) => {
  // Build services array with timing from slot
  const servicesPayload = services.map((service, index) => ({
    service_id: String(service.id),
    variation_id: service.selectedVariation?.id || service.variation_id || null,
    quantity: 1,
    professional_id: assignments[String(index)],
    custom_price: null,
    custom_duration: null,
    start_time: selectedSlot.services?.[index]?.start_time || selectedSlot.start_time,
    end_time: selectedSlot.services?.[index]?.end_time || null
  }));

  return {
    client: {
      id: client.id || null,
      name: client.name || '',
      nickname: client.nickname || null,
      phone: client.phone || client.phone_number || null,
      email: client.email || null,
      cpf: client.cpf || null,
      address_street: client.address_street || null,
      address_number: client.address_number || null,
      address_complement: client.address_complement || null,
      address_neighborhood: client.address_neighborhood || null,
      address_city: client.address_city || null,
      address_state: client.address_state || null,
      address_cep: client.address_cep || null
    },
    services: servicesPayload,
    appointment_date: validateDate(date),
    start_time: selectedSlot.start_time?.substring(0, 5) || selectedSlot.time, // HH:MM format
    execution_preference: 'sequential', // Backend determines actual execution
    notes: notes || null
  };
};

/**
 * Create appointment using scheduled appointments API
 * @param {Object} bookingData - Complete booking payload from buildBookingPayloadV2
 * @returns {Promise<Object>} Created appointment data
 */
export const createAppointment = async (bookingData) => {
  const endpoint = buildApiEndpoint('appointments/scheduled');

  return withApiErrorHandling(
    () => apiClient.post(endpoint, bookingData),
    {
      defaultValue: null,
      transformData: (data) => data
    }
  );
};

// Export all functions as named exports and as default object
const wizardApiService = {
  getAvailableDatesForCalendar,
  getAvailableProfessionals,
  getAvailableSlots,
  createMultiServiceBooking,
  buildBookingRequest,
  buildWizardBookingPayload,
  validateDate,
  formatServicesForBooking,
  // V2 API functions
  getAvailabilityV2,
  transformV2SlotsForUI,
  buildBookingPayloadV2,
  createAppointment
};

export default wizardApiService;